import React, { useEffect, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { login } from '../../store/reducers/userSlice';
import { patchApi } from '../../api';
import Button from '../ui/Button';
import useFetch from '../../utils/useFetch';

// Disabled fields are never sent, so they must not be validated: the phone
// number is always read-only here, and a verified account's PAN is locked.
const buildSchema = (panLocked) => Yup.object().shape({
	email: Yup.string()
		.email('Invalid email')
		.max(254, 'Email cannot exceed 254 characters')
		.nullable(),
	given_name: Yup.string()
		.min(1, 'Given name is required')
		.max(255, 'Given name cannot exceed 255 characters')
		.required('Given name is required'),
	family_name: Yup.string()
		.min(1, 'Family name is required')
		.max(255, 'Family name cannot exceed 255 characters')
		.required('Family name is required'),
	phone_number2: Yup.string()
		.max(30, 'Phone number cannot exceed 30 characters')
		.nullable(),
	address: Yup.string()
		.nullable(),
	...(panLocked ? {} : {
		pan_number: Yup.string()
			.min(1, 'PAN number is required')
			.max(255, 'PAN number cannot exceed 255 characters')
			.required('PAN number is required'),
	}),
});

const UpdateProfile = () => {
	const dispatch = useDispatch();
	const { user } = useSelector((state) => state.user);
	const [selectedImage, setSelectedImage] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [selectedFileName, setSelectedFileName] = useState('');
	const [businessRegFile, setBusinessRegFile] = useState(null);
	const [businessRegName, setBusinessRegName] = useState('');
	const [verificationDocFile, setVerificationDocFile] = useState(null);
	const [verificationDocName, setVerificationDocName] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	const { data, loading, error } = useFetch(`system/users/${user?.user?.uuid}/`);
	// The PAN and the documents are what staff checked at verification, so an
	// approved account can no longer change them — the backend rejects both
	// (#136, #150).
	const verified = Boolean(data?.is_verified);

	const convertToBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				resolve(reader.result);
			};
			reader.onerror = (error) => {
				reject(error);
			};
		});
	};

	const handleSubmit = async (values) => {
		setIsLoading(true);
		try {
			// Only include fields that have changed
			let updateData = {};
			
			// Compare current values with initial data
			if (values.email !== data?.email) updateData.email = values.email || '';
			if (values.given_name !== data?.given_name) updateData.given_name = values.given_name;
			if (values.family_name !== data?.family_name) updateData.family_name = values.family_name;
			if (values.phone_number2 !== data?.phone_number2) updateData.phone_number2 = values.phone_number2 || '';
			if (values.address !== data?.address) updateData.address = values.address || '';
			if (!verified && values.pan_number !== data?.pan_number) updateData.pan_number = values.pan_number;
			
			// Always include these fields for identification
			updateData.uuid = user?.user?.uuid;
			updateData.id = user?.user?.id;
			
			// Only include a document in updateData if a new file is selected.
			try {
				if (selectedFile) {
					updateData.profile_image = await convertToBase64(selectedFile);
				}
				if (businessRegFile) {
					updateData.business_registration = await convertToBase64(businessRegFile);
				}
				if (verificationDocFile) {
					updateData.verification_document = await convertToBase64(verificationDocFile);
				}
			} catch (imageError) {
				console.error('Error converting file:', imageError);
				toast.error('Failed to process file. Please try again.');
				setIsLoading(false);
				return;
			}

			const resubmittedDocs = !!(businessRegFile || verificationDocFile) ||
				(updateData.pan_number !== undefined && updateData.pan_number !== data?.pan_number);

			// Don't make API call if no data has changed
			if (Object.keys(updateData).length <= 2 && !selectedFile &&
				!businessRegFile && !verificationDocFile) {
				toast.info('No changes detected');
				setIsLoading(false);
				return;
			}

			const response = await patchApi(`system/users/${user?.user?.uuid}/`, updateData);
			
			// Update Redux store with only changed fields
			const updatedUserData = {
				...user,
				user: {
					...user.user,
					...updateData,
					// Only update profile_image in Redux if a new image was uploaded
					profile_image: selectedFile ? updateData.profile_image : user.user.profile_image
				}
			};
			dispatch(login(updatedUserData));

			toast.success(
				resubmittedDocs
					? 'Documents submitted — your account will be re-reviewed.'
					: 'Profile updated successfully'
			);
			setBusinessRegFile(null);
			setBusinessRegName('');
			setVerificationDocFile(null);
			setVerificationDocName('');
		} catch (error) {
			console.error('Update error:', error);
			toast.error(
				error?.response?.data?.message || 
				error?.response?.data?.error || 
				'Failed to update profile'
			);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const handleImageChange = (event) => {
		const file = event.target.files[0];
		if (file) {
			// Validate file type
			if (!file.type.match('image/(jpeg|png)')) {
				toast.error('Please select only JPEG or PNG images');
				return;
			}
			// Validate file size (e.g., max 5MB)
			if (file.size > 5 * 1024 * 1024) {
				toast.error('Image size should be less than 5MB');
				return;
			}
			setSelectedImage(URL.createObjectURL(file));
			setSelectedFile(file);
			setSelectedFileName(file.name);
		}
	};

	const handleDocumentChange = (event, setFile, setName) => {
		const file = event.target.files[0];
		if (!file) return;
		if (!file.type.match('image/(jpeg|png)')) {
			toast.error('Please select only JPEG or PNG images');
			return;
		}
		if (file.size > 5 * 1024 * 1024) {
			toast.error('File size should be less than 5MB');
			return;
		}
		setFile(file);
		setName(file.name);
	};

	if (loading) {
		return <div className="text-center py-10">Loading...</div>;
	}

	if (error) {
		return <div className="text-center py-10 text-red-500">Error loading profile data</div>;
	}

	return (
		<div className="max-w-screen-2xl">
			<div className="md:grid md:grid-cols-3 md:gap-6">
				<div className="md:col-span-1">
					<div className="px-4 sm:px-0">
						<h2 className="text-xl font-semibold mb-5 text-black">
							Update Profile
						</h2>
					</div>
				</div>
			</div>

			<div className="mt-5 md:mt-0">
				<Formik
					enableReinitialize
					initialValues={{
						address: data?.address || '',
						email: data?.email || '',
						given_name: data?.given_name || '',
						family_name: data?.family_name || '',
						phone_number: data?.phone_number || '',
						phone_number2: data?.phone_number2 || '',
						pan_number: data?.pan_number || '',
					}}
					validationSchema={buildSchema(verified)}
					onSubmit={handleSubmit}
				>
					{({ errors, touched }) => (
						<Form>
							<div className="bg-white space-y-6">
								{/* Image Upload Section */}
								<div>
									<label htmlFor="image" className="block text-gray-500 font-medium text-sm leading-none mb-2">
										Profile Photo
									</label>
									<div className="mt-1 flex items-center">
										<div className="w-full text-center">
											<label tabIndex="0" className="flex flex-col w-full h-fit px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md cursor-pointer">
												{selectedImage || data?.profile_image ? (
													<img
														src={selectedImage || data?.profile_image}
														alt="Selected"
														className="max-h-32 max-w-32 mx-auto"
													/>
												) : (
													<>
														<span className="mx-auto flex justify-center">
															<svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" className="text-3xl text-brand-500" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
																<polyline points="16 16 12 12 8 16"></polyline>
																<line x1="12" y1="12" x2="12" y2="21"></line>
																<path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"></path>
															</svg>
														</span>
														<p className="text-sm mt-2 text-black">Drag your image here</p>
														<em className="text-xs text-gray-400">Only *.jpeg and *.png images will be accepted</em>
													</>
												)}
												<Field
													id="image"
													name="image"
													accept="image/jpeg, image/png"
													type="file"
													tabIndex="-1"
													style={{ display: 'none' }}
													autoComplete="off"
													onChange={handleImageChange}
												/>
											</label>
											{selectedFileName && (
												<aside className="flex flex-row flex-wrap mt-4">
													<div className="inline-flex border rounded-md border-gray-100 w-24 max-h-24 p-2">
														<p className="text-semibold text-sm">{selectedFileName}</p>
													</div>
												</aside>
											)}
										</div>
									</div>
								</div>
							</div>

							{/* Form Fields */}
							<div className="mt-10">
								<div className="bg-white">
									<div className="grid grid-cols-6 gap-6">
										<div className="col-span-6 sm:col-span-3">
											<label htmlFor="given_name" className="block text-gray-500 font-medium text-sm leading-none mb-2">
												Given Name *
											</label>
											<Field
												id="given_name"
												name="given_name"
												placeholder="Given Name"
												className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
											/>
											{errors.given_name && touched.given_name && (
												<div className="text-red-500 text-sm mt-1">{errors.given_name}</div>
											)}
										</div>

										<div className="col-span-6 sm:col-span-3">
											<label htmlFor="family_name" className="block text-gray-500 font-medium text-sm leading-none mb-2">
												Family Name *
											</label>
											<Field
												id="family_name"
												name="family_name"
												placeholder="Family Name"
												className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
											/>
											{errors.family_name && touched.family_name && (
												<div className="text-red-500 text-sm mt-1">{errors.family_name}</div>
											)}
										</div>

										<div className="col-span-6 sm:col-span-3">
											<label htmlFor="email" className="block text-gray-500 font-medium text-sm leading-none mb-2">
												Email Address
											</label>
											<Field
												id="email"
												name="email"
												type="email"
												placeholder="Your Email"
												className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
											/>
											{errors.email && touched.email && (
												<div className="text-red-500 text-sm mt-1">{errors.email}</div>
											)}
										</div>

										<div className="col-span-6 sm:col-span-3">
											<label htmlFor="phone_number" className="block text-gray-500 font-medium text-sm leading-none mb-2">
												Phone Number *
											</label>
											<Field
												id="phone_number"
												name="phone_number"
												placeholder="Your Phone Number"
												disabled={true}
												className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-gray-100 border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12 cursor-not-allowed"
											/>
										</div>

										<div className="col-span-6 sm:col-span-3">
											<label htmlFor="phone_number2" className="block text-gray-500 font-medium text-sm leading-none mb-2">
												Alternative Phone Number
											</label>
											<Field
												id="phone_number2"
												name="phone_number2"
												placeholder="Alternative Phone Number"
												className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
											/>
											{errors.phone_number2 && touched.phone_number2 && (
												<div className="text-red-500 text-sm mt-1">{errors.phone_number2}</div>
											)}
										</div>

										<div className="col-span-6 sm:col-span-3">
											<label htmlFor="pan_number" className="block text-gray-500 font-medium text-sm leading-none mb-2">
												PAN Number *
											</label>
											<Field
												id="pan_number"
												name="pan_number"
												placeholder="Your PAN Number"
												disabled={verified}
												className={`py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12 ${verified ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
											/>
											{verified && (
												<div className="text-gray-500 text-sm mt-1">
													Locked because your account is verified. Contact support if it needs correcting.
												</div>
											)}
											{errors.pan_number && touched.pan_number && (
												<div className="text-red-500 text-sm mt-1">{errors.pan_number}</div>
											)}
										</div>

										{/* Approved accounts don't re-upload: a replacement would send
											them back for review (#150). */}
										{!verified && (
											<>
												<div className="col-span-6 sm:col-span-3">
													<label htmlFor="business_registration" className="block text-gray-500 font-medium text-sm leading-none mb-2">
														Business Registration
													</label>
													<input
														id="business_registration"
														name="business_registration"
														type="file"
														accept="image/jpeg, image/png"
														onChange={(e) => handleDocumentChange(e, setBusinessRegFile, setBusinessRegName)}
														className="py-2 px-4 md:px-5 w-full appearance-none border text-sm text-input rounded-md min-h-12 bg-white border-gray-200 focus:outline-none focus:border-brand-500"
													/>
													{businessRegName && (
														<p className="text-xs text-gray-500 mt-1">Selected: {businessRegName}</p>
													)}
												</div>

												<div className="col-span-6 sm:col-span-3">
													<label htmlFor="verification_document" className="block text-gray-500 font-medium text-sm leading-none mb-2">
														Verification Document
													</label>
													<input
														id="verification_document"
														name="verification_document"
														type="file"
														accept="image/jpeg, image/png"
														onChange={(e) => handleDocumentChange(e, setVerificationDocFile, setVerificationDocName)}
														className="py-2 px-4 md:px-5 w-full appearance-none border text-sm text-input rounded-md min-h-12 bg-white border-gray-200 focus:outline-none focus:border-brand-500"
													/>
													{verificationDocName && (
														<p className="text-xs text-gray-500 mt-1">Selected: {verificationDocName}</p>
													)}
												</div>
											</>
										)}

										<div className="col-span-6">
											<p className="text-xs text-gray-500">
												{verified
													? 'Your PAN and verification documents are on file. Contact support if they need updating.'
													: 'Changing your PAN or re-uploading a document sends your account back for verification review.'}
											</p>
										</div>

										<div className="col-span-6">
											<label htmlFor="address" className="block text-gray-500 font-medium text-sm leading-none mb-2">
												Address
											</label>
											<Field
												id="address"
												name="address"
												placeholder="Your Address"
												className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
											/>
											{errors.address && touched.address && (
												<div className="text-red-500 text-sm mt-1">{errors.address}</div>
											)}
										</div>
									</div>

									<div className="mt-5 text-right">
										<Button type="submit" size="lg" disabled={isLoading} className="mt-1 w-full sm:w-auto">
											{isLoading ? 'Updating Profile...' : 'Update Profile'}
										</Button>
									</div>
								</div>
							</div>
						</Form>
					)}
				</Formik>
			</div>
		</div>
	);
};

export default UpdateProfile;